import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * @param {Event} event
   */
  async loadMore(event) {
    const { loadMoreButton, grid } = this.refs;
    if (loadMoreButton instanceof HTMLButtonElement) loadMoreButton.disabled = true;

    const skeletons = this.#addSkeletons(grid);

    await this.renderNextPage();

    for (const skeleton of skeletons) skeleton.remove();

    this.#updateLoadMoreButtonVisibility();
  }

  /**
   * @param {HTMLUListElement} [grid]
   * @returns {HTMLLIElement[]}
   */
  #addSkeletons(grid) {
    if (!grid) return [];

    const skeletonCount = 4;
    /** @type {HTMLLIElement[]} */
    const skeletons = [];

    for (let i = 0; i < skeletonCount; i++) {
      const skeleton = document.createElement('li');
      skeleton.className = 'product-grid__item product-grid__item--skeleton';
      skeleton.setAttribute('aria-hidden', 'true');
      grid.append(skeleton);
      skeletons.push(skeleton);
    }

    return skeletons;
  }

  #updateLoadMoreButtonVisibility() {
    const { grid, loadMoreButton, cards } = this.refs;
    if (!(loadMoreButton instanceof HTMLButtonElement) || !grid) return;

    const lastPage = Number(grid.dataset.lastPage);
    const lastCard = Array.isArray(cards) ? cards[cards.length - 1] : undefined;
    const currentPage = lastCard ? Number(lastCard.dataset.page) : 1;

    loadMoreButton.disabled = false;
    loadMoreButton.hidden = currentPage >= lastPage;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
